import React, { useEffect, useRef, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useRouteLoaderData } from '@remix-run/react';
import { useStore } from '@nanostores/react';
import type { ExamplePrompt } from '~/utils/examplePrompts';
import { FRAMEWORK_CONFIGS } from '~/lib/stores/techStackSelection';
import { userStore } from '~/lib/stores/user';
import type { ServerLoaderData } from '~/root';
import { usePosthogEvent } from '~/lib/hooks/posthog/usePosthogEvent';
import { useTranslation } from '~/lib/hooks/useTranslation';

interface TemplateDetailsModalProps {
  isOpen: boolean;
  template: ExamplePrompt | null;
  onClose: () => void;
  onRemix: (template: ExamplePrompt) => void;
}

export const TemplateDetailsModal: React.FC<TemplateDetailsModalProps> = ({ isOpen, template, onClose, onRemix }) => {
  const contentRef = useRef<HTMLDivElement>(null);
  const [isBuying, setIsBuying] = useState(false);
  const { trackButtonClick } = usePosthogEvent();
  const { t } = useTranslation();

  const rootData = useRouteLoaderData('root') as ServerLoaderData;
  const token = rootData?.token || '';
  const paymentUrl = rootData?.paymentUrl || 'https://payments.dualite.dev';
  const currency = rootData?.currency || 'USD';
  const finalCurrency = currency === 'INR' ? 'INR' : 'USD';

  const userData = useStore(userStore);
  const ownedPaidTemplateKeys = userData.ownedPaidTemplateKeys || [];

  // For paid templates, determine ownership status using template.templateKey
  const ownedEntry = template?.isPaid
    ? ownedPaidTemplateKeys.find((entry) => entry.templateName === template.templateKey)
    : undefined;
  const isPending = ownedEntry?.status === 'PENDING';
  const isActive = ownedEntry?.status === 'ACTIVE';

  const handleBuy = (templateKey: string) => {
    if (!template) {
      return;
    }

    if (!template) {
      return;
    }

    setIsBuying(true);

    const templateNameInLowerCase = templateKey;

    //    debugger;
    window.location.href = `${paymentUrl}/template?token=${token}&currency=USD&selectedTemplateName=${templateNameInLowerCase}`;
  };

  const getCtaLabel = (): string | null => {
    if (isBuying) {
      return null;
    }

    if (template?.isPaid) {
      if (isActive) {
        return 'Use';
      }

      if (isPending) {
        return `Continue Purchase for $${template.price}`;
      }

      return `Buy for $${template.price}`;
    }

    return 'Use for Free';
  };

  const handleCtaClick = () => {
    if (!template) {
      return;
    }

    const cta = getCtaLabel();

    if (cta) {
      trackButtonClick(cta, {
        template_id: template.templateKey,
        template_name: template.titleDefault,
        is_paid: template.isPaid,
        is_active: isActive,
        is_pending: isPending,
      });
    }

    if (template.isPaid) {
      if (isActive) {
        onRemix(template);
      } else {
        handleBuy(template.templateKey);
      }
    } else {
      onRemix(template);
    }
  };

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleKeyDown);
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = '';
    };
  }, [isOpen, onClose]);

  if (!template) {
    return null;
  }

  const frameworkLabel = FRAMEWORK_CONFIGS[template.framework]?.label || 'React + Tailwind';
  const hasPreview = !!template.previewLink;

  return (
    <AnimatePresence>
      {isOpen && (
        <div className="fixed inset-0 z-9999 flex items-center justify-center p-4">
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="fixed inset-0 bg-black/60 backdrop-blur-md"
            onClick={onClose}
          />

          {/* Modal */}
          <motion.div
            initial={{ opacity: 0, scale: 0.96, y: 12 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.96, y: 12 }}
            transition={{ duration: 0.22, ease: [0.22, 1, 0.36, 1] }}
            className="relative z-10 w-full max-w-[1080px] max-h-[88vh] rounded-2xl overflow-hidden border border-dualite-border-primary"
            style={{
              background: 'var(--dualite-background-secondary)',
              boxShadow:
                '0 0 0 1px rgba(255,255,255,0.03), 0 24px 48px -12px rgba(0, 0, 0, 0.45), 0 8px 16px -4px rgba(0, 0, 0, 0.2)',
            }}
          >
            {/* Scrollable content */}
            <div
              ref={contentRef}
              className="overflow-y-auto max-h-[88vh]"
              style={{
                scrollbarWidth: 'thin',
                scrollbarColor: 'var(--dualite-scrollbar-thumb) transparent',
              }}
            >
              {/* ─── Banner Image ─── */}
              <div className="relative w-full h-56 sm:h-72 overflow-hidden">
                <img
                  src={template.screenshotLinks[0]}
                  alt={template.titleDefault}
                  className="w-full h-full object-cover object-top"
                />
                {/* bottom fade */}
                <div
                  className="absolute inset-x-0 bottom-0 h-24"
                  style={{
                    background: 'linear-gradient(to top, var(--dualite-background-secondary) 0%, transparent 100%)',
                  }}
                />

                {/* Close button */}
                <button
                  onClick={onClose}
                  className="absolute top-3.5 right-3.5 w-8 h-8 flex items-center justify-center rounded-lg bg-black/40 backdrop-blur-md border border-white/10 hover:bg-black/60 transition-colors duration-150"
                  aria-label="Close modal"
                >
                  <div className="i-ph:x text-white/90 text-sm" />
                </button>
              </div>

              {/* ─── Body ─── */}
              <div className="px-7 sm:px-9 py-9 -mt-4 relative">
                {/* Title Row with CTAs on far right */}
                <div className="flex items-start gap-4 mb-6">
                  <h2 className="text-xl sm:text-2xl font-semibold text-dualite-text-primary leading-snug flex-1 min-w-0">
                    {t(template.titleKey, { defaultValue: template.titleDefault })}
                  </h2>

                  {/* CTAs — far right */}
                  <div className="flex flex-col items-end gap-1.5 shrink-0">
                    <div className="flex items-center gap-3">
                      <a
                        href={hasPreview ? template.previewLink : '#'}
                        target={hasPreview ? '_blank' : undefined}
                        rel={hasPreview ? 'noopener noreferrer' : undefined}
                        onClick={(e) => {
                          if (!hasPreview) {
                            e.preventDefault();
                          }
                        }}
                        className={`inline-flex items-center gap-2 px-5 py-2.5 rounded-lg text-[13px] font-semibold border transition-all duration-200 ${
                          hasPreview
                            ? 'bg-dualite-background-button hover:bg-dualite-background-button-hover border-dualite-border-primary hover:border-dualite-border-secondary text-dualite-text-primary cursor-pointer active:scale-[0.98]'
                            : 'bg-dualite-background-button/50 border-dualite-border-primary text-dualite-text-tertiary cursor-not-allowed'
                        }`}
                      >
                        Preview
                      </a>

                      <button
                        onClick={handleCtaClick}
                        disabled={isBuying}
                        className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg text-[13px] font-semibold transition-all duration-200 hover:shadow-lg active:scale-[0.98] disabled:opacity-70 disabled:cursor-not-allowed"
                        style={{
                          background: 'var(--dualite-background-cta)',
                          color: 'var(--dualite-text-cta)',
                        }}
                      >
                        {isBuying ? <div className="i-ph:spinner-gap animate-spin text-base" /> : getCtaLabel()}
                      </button>
                    </div>
                    {template.isPaid && isActive && (
                      <span className="text-[11px] text-green-400/80 font-medium">
                        ✓ You have purchased this template
                      </span>
                    )}
                  </div>
                </div>

                {/* ─── Full-width Screenshot Grid (2x2, no heading, no hover animation) ─── */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-8">
                  {template.screenshotLinks.slice(1).map((link, idx) => (
                    <div key={idx} className="relative rounded-lg overflow-hidden border border-dualite-border-primary">
                      <img
                        src={link}
                        alt={`${template.titleDefault} — screenshot ${idx + 2}`}
                        className="w-full aspect-16/10 object-cover"
                        loading="lazy"
                      />
                    </div>
                  ))}
                </div>

                {/* ─── Two-column: About (left) + Details (right) ─── */}
                <div className="grid grid-cols-1 lg:grid-cols-5 gap-8">
                  {/* Left side — textDefault + aboutTemplate + features */}
                  <div className="lg:col-span-3">
                    {/* Subheading — textDefault */}
                    <p className="text-[14px] sm:text-[15px] text-dualite-text-secondary mb-7">
                      {t(template.textKey, { defaultValue: template.textDefault })}
                    </p>

                    {/* About the Template */}
                    {/* <h3 className="text-[15px] font-semibold text-dualite-text-primary mb-4">About the Template</h3>
                    <p className="text-[13px] sm:text-sm text-dualite-text-secondary mb-7 leading-loose">
                      {template.aboutTemplate}
                    </p> */}

                    {/* Features list */}
                    {template.featuresList.length > 0 && (
                      <ul className="space-y-3 mt-2">
                        {template.featuresList.map((feature, idx) => (
                          <li
                            key={idx}
                            className="flex items-start gap-2.5 text-[13px] text-dualite-text-secondary leading-relaxed"
                          >
                            <div
                              className="i-ph:check-circle text-sm shrink-0 mt-[2px]"
                              style={{ color: 'var(--dualite-text-primary)' }}
                            />
                            <span>{feature}</span>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>

                  {/* Right side — Details */}
                  <div className="lg:col-span-2">
                    <h3 className="text-[15px] font-semibold text-dualite-text-primary mb-4">Categories</h3>

                    <ul className="space-y-3">
                      <li className="flex items-center gap-2.5 text-[13px] text-dualite-text-secondary">
                        <div className="i-ph:code text-sm shrink-0" style={{ color: 'var(--dualite-text-primary)' }} />
                        {frameworkLabel}
                      </li>
                      <li className="flex items-center gap-2.5 text-[13px] text-dualite-text-secondary capitalize">
                        <div
                          className="i-ph:squares-four text-sm shrink-0"
                          style={{ color: 'var(--dualite-text-primary)' }}
                        />
                        {template.category || 'General'}
                      </li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
};
